import { Paper, Popover as MuiPopover } from "@mui/material";

import { Box } from "ui/box/box";
import { Grow } from "ui/grow/grow";
import { mergeSx } from "ui/merge-sx";
import { Popper } from "ui/popper/popper";
import { PopoverCloseProvider } from "ui/popover/popover-context";
import {
  DEFAULT_GUTTER_PX,
  DEFAULT_PADDING,
  DEFAULT_WIDTH_PX,
  MAX_HEIGHT_PX,
} from "ui/popover/popover.constants";
import { PLACEMENT_ORIGINS } from "ui/popover/popover.helpers";
import {
  getBodySx,
  getPaperSx,
  POPPER_SX,
  PopoverArrow,
  TRIGGER_SX,
} from "ui/popover/popover.styles";
import { type PopoverProps } from "ui/popover/popover.types";
import { usePopover } from "ui/popover/use-popover";

export const Popover = ({
  arrow = false,
  children,
  padding = DEFAULT_PADDING,
  gutter = DEFAULT_GUTTER_PX,
  placement = "bottom",
  triggerElement,
  triggerPosition,
  anchorEl: externalAnchorEl,
  disableAutoFocus,
  width = DEFAULT_WIDTH_PX,
  maxHeight = MAX_HEIGHT_PX,
  open: controlledOpen,
  onOpen,
  onClose,
  keepPageInteractive = false,
  sx,
  ref,
  "data-testid": dataTestId,
  ...paperProps
}: PopoverProps) => {
  const {
    open,
    anchorEl,
    setAnchorEl,
    paperRef,
    handleClose,
    handleTriggerClick,
    offset,
    skidding,
    popperModifiers,
  } = usePopover({
    open: controlledOpen,
    onOpen,
    onClose,
    arrow,
    gutter,
    keepPageInteractive,
    ref,
    anchorEl: externalAnchorEl,
    disableAutoFocus,
    placement,
  });

  const paperSx = mergeSx(
    getPaperSx({
      arrow,
      offset,
      skidding,
      keepPageInteractive,
      maxHeight,
      placement,
      width,
    }),
    sx,
  );

  const panelContent = (
    <PopoverCloseProvider value={handleClose}>
      {arrow && <PopoverArrow aria-hidden placement={placement} />}
      <Box sx={getBodySx(padding)}>{children}</Box>
    </PopoverCloseProvider>
  );

  return (
    <>
      {externalAnchorEl === undefined && (
        <Box ref={setAnchorEl} sx={mergeSx(TRIGGER_SX, triggerPosition)} onClick={handleTriggerClick}>
          {triggerElement}
        </Box>
      )}
      {keepPageInteractive ? (
        <Popper
          data-testid={dataTestId}
          open={open && Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement={placement}
          modifiers={popperModifiers}
          role="presentation"
          sx={POPPER_SX}
          transition>
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} timeout="auto">
              <Paper {...paperProps} ref={paperRef} sx={paperSx} tabIndex={-1}>
                {panelContent}
              </Paper>
            </Grow>
          )}
        </Popper>
      ) : (
        <MuiPopover
          data-testid={dataTestId}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          {...PLACEMENT_ORIGINS[placement]}
          disableScrollLock
          slotProps={{ paper: { ...paperProps, ref: paperRef, sx: paperSx } }}>
          {panelContent}
        </MuiPopover>
      )}
    </>
  );
};
